/* eslint-disable no-fallthrough */
import store from "@store/index";
import func from "@uikit/func";
import { useDispatch } from "react-redux";
import { editorActions } from "@store/editor";
import { getIt } from "@uikit/getIt";
import { get, isEmpty } from "lodash";
import useFeatureTags from "@custom-hooks/useFeatureTags";
import skipEmptyVerification from "../Nodes/Agent/skipEmptyVerification";
import useSetNodeErrorTips from "@custom-hooks/useSetNodeErrorTips";
import { distance, selectApiReg } from "./homeConst";
import useHandleOtherTypeRun from "@custom-hooks/useHandleOtherTypeRun";
import { Api } from "@uikit/service/api";
import { CreatorStoreMethods } from "@uikit/service/CreatorStoreMethods";
import { defaultApis, getFunctionNameByAlias } from "@views/thinking-layout-editor/nodeTypeComponents/nodeTypeDispose";
import useHandleChatBox from "@custom-hooks/useHandleChatBox";
import { GROUPID_REG, IMAGE_TYPES, STOCK_TYPES } from "@views/thinking-layout-editor/constants";
import { MessageCenter } from "imagica-corekit/dist/base/kernel/MessageCenter";
import useOpenAiParam from "@custom-hooks/useOpenAiParam";
import usePostUrlCanAbort from "@custom-hooks/usePostUrlCanAbort";
import { FotClient } from "imagica-corekit/dist/base/api/FotClient";
import useShowStockInterfaceFunc from "@custom-hooks/useShowStockInterfaceFunc";
import useHandleAskBrainData from "@custom-hooks/useHandleAskBrainData";
import utilIndex from "@views/thinking-layout-editor/utils/index";
import { arrayKeyToObject, getAutomaticDisplayType, splitText } from "@views/thinking-layout-editor/util";
import { getHtmlStr, handleVarInject } from "./HomeRunUtil";
import { CustomEdgeUtil } from "@uikit/util/CustomEdgeUtil";
import { settings } from "imagica-corekit/dist/base/kernel/Settings";
import useConnectAgentNode from "../Nodes/Agent/useConnectAgentNode";
import { HomeUsePluginUtil } from "@uikit/util/HomeUsePluginUtil";
import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { customFunctionStore } from "./store/CustomFunctionStore";
import { creatorRefStore } from "./store/CreatorRefStore";
import { projectPageStore } from "./store/ProjectPageStore";
import { HomeMethodsService } from "@uikit/service/HomeMethodsService";
import { CreatorNodesStore } from "@uiview/views/ProjectCanvas/store/CreatorNodesStore";
import { RestaurantResponseData } from "imagica-corekit/dist/base/api/foodTyped/Restaurant";
import { CreatorEdgesStore } from "@uiview/views/ProjectCanvas/store/CreatorEdgesStore";
import { CanvasDataRef } from "@uikit/model/CanvasDataRef";

// eslint-disable-next-line
export function useHomeServices() {
  const api = new Api(false);
  const messageCenter = getIt(MessageCenter);
  const brainClient = getIt(FotClient);
  const homeMethods = getIt(HomeMethodsService);
  const creatorNodesStore = getIt(CreatorNodesStore);
  const creatorEdgesStore = getIt(CreatorEdgesStore);

  const canvasDataRef = getIt(CanvasDataRef);
  const groupDataRef = canvasDataRef.groupDataRef;
  const edgeDataRef = canvasDataRef.edgeDataRef;

  const creatorStoreMethods = getIt(CreatorStoreMethods);
  const feature_tags = useFeatureTags();

  const dispatch = useDispatch();

  const setNeedDisableEdgeIds = (val: string[]): void => {
    dispatch(editorActions.setNeedDisableEdgeIds(val));
  };
  const setShowTooltip = (val: string): void => {
    dispatch(editorActions.setShowTooltip(val));
  };
  const setCloseToolTipArr = (val: string[]): void => {
    dispatch(editorActions.setCloseToolTipArr(val));
  };
  const setReloadMap = (val: boolean): void => {
    dispatch(editorActions.setReloadMap(val));
  };

  const { getOpenAiParam, getCompletionsGenerationParam } = useOpenAiParam();
  const { setChatBoxParam } = useHandleChatBox();
  const { postData, postWithStreaming } = usePostUrlCanAbort();
  const { setConnectAgentNode } = useConnectAgentNode();

  const { setNodeErrorTip } = useSetNodeErrorTips();
  const { handleUploadFileRun } = useHandleOtherTypeRun({
    getSourceOfTruthResults,
    setEdgeLineParam: creatorStoreMethods.setEdgeLineParam,
  });
  const { handleStockInterfaceFunc } = useShowStockInterfaceFunc({
    postAIDescription,
    setNodeParam: creatorStoreMethods.setNodeParam,
    setNodeErrorTip,
  });
  const { getAskBrainData } = useHandleAskBrainData({
    postWithStreaming,
    setNodeParam: creatorStoreMethods.setNodeParam,
  });

  // conts
  //==== functions

  const sendRequest = async (
    currentSourceNodeValue: any,
    requestData: { url: any },
    setLoadingFunc = (): any => {}
  ): Promise<any> => {
    if (func.isEmpty(requestData.url)) return;
    const params = handleVarInject(currentSourceNodeValue, requestData);
    if (func.isEmpty(params)) return;
    const { url, method, headerList, bodyParam } = params as any;
    const headers = {} as any;
    headerList.forEach((x: any) => {
      headers[x.name] = x.value;
    });
    const start = Date.now();
    let result = null;
    try {
      // @ts-expect-error 修复ts类型报错
      setLoadingFunc(true);
      const res = await api.commonRequest(url, method.toUpperCase(), {
        headers,
        body: bodyParam,
      });
      if (!res.ok) {
        throw res;
      }
      if (store.getState().editor.createAPIFuncData.open) {
        result = res;
      } else {
        await res.json().then((r: any) => {
          result = r;
        });
      }
    } catch (error: any) {
      if (!func.isEmpty(error.name)) {
        result = error.response;
      }
      console.error("send Request", error);
      return Promise.reject(error);
    } finally {
      // @ts-expect-error 修复ts类型报错
      setLoadingFunc(false);
      if (store.getState().editor.createAPIFuncData.open) {
        // eslint-disable-next-line
        return await utilIndex.handleResponseStatusData(result, start);
      }
      // eslint-disable-next-line
      return {
        data: { type: "json", value: result },
      };
    }
  };

  const methodType = async (currentSingleFlow: any, nodeText: any): Promise<any> => {
    try {
      let obj = {};
      switch (currentSingleFlow?.type) {
        case "CustomCode": {
          const htmlSrc = getHtmlStr(currentSingleFlow, nodeText);
          obj = {
            textAreaValue: htmlSrc,
            displayType: "customJsFunction",
            loadingNodeHtml: true,
          };
          break;
        }
        case "customApi": {
          const requestData = currentSingleFlow.apiData.requestData;
          const responseData = await sendRequest(nodeText, requestData);
          obj = {
            textAreaValue: responseData || {},
            displayType: "customApiFunction",
          };
          // opt:
          break;
        }

        default:
          break;
      }
      return obj;
    } catch (error) {
      return Promise.reject(error);
    }
  };
  const normalCall = async (
    lineParams: { lineType: string; identifier: { value: any }; queryValue: any },
    nodeText: any,
    takeLastDisplay: boolean,
    edgeParam: { id: any; target: any; source: any; lineParam?: any },
    isRecursiveCall = false
  ): Promise<string> => {
    const { source, target, id } = edgeParam;
    try {
      let postParam = {};
      let url = "/be/bas-demo-v4/nlp/brain_openai";
      if (lineParams.lineType === "identifier") {
        url = `/${takeLastDisplay ? "stream" : "be"}/bas-demo-v4/nlp/completions_generation`;
        postParam = await getCompletionsGenerationParam(
          "object_id",
          lineParams.identifier.value,
          {
            input: nodeText,
          },
          takeLastDisplay ? true : false
        );
      } else {
        postParam = getOpenAiParam({
          lineParams,
          prompt: lineParams.queryValue || "",
          nodeText,
        });
      }

      let temp = "";
      await postWithStreaming(
        url,
        postParam,
        (msg: any) => {
          temp += msg.choices?.[0]?.text || msg.choices?.[0]?.delta?.content || "";
          // takeLastDisplay，作为自定义func最后一项时才使用streaming输出
          // isRecursiveCall 是最外部的自定义func调用
          if (takeLastDisplay && !isRecursiveCall) {
            creatorNodesStore.setNodes(prevNodes => {
              return prevNodes.map(l => {
                const displayType = getAutomaticDisplayType(l.data, temp);
                if (l.id === target) {
                  return {
                    ...l,
                    data: {
                      ...l.data,
                      textAreaValue: temp,
                      displayType,
                    },
                  };
                }
                return l;
              });
            });
          }
        },
        false,
        ((postInstance: any) => {
          creatorStoreMethods.setNodeParam({
            id: target,
            data: {
              showStopGenerateBtn: true,
              abortNode: {
                source,
                target,
                edgeId: id,
                apiInstance: postInstance,
              },
            },
          });
        }) as any
      );

      return temp;
    } catch (error) {
      return Promise.reject(error);
    } finally {
      creatorStoreMethods.setNodeParam({
        id: target,
        data: {
          showStopGenerateBtn: false,
          abortNode: {},
        },
      });
    }
  };
  const customMethod = async (
    nodeText: any,
    customFunc: { type: any; edgeArr?: string | any[] | undefined; apiData?: any },
    lineParams: any,
    takeLastDisplay: boolean,
    edgeParam: { id?: any; target: any; source?: any; lineParam?: any }
  ): Promise<any> => {
    try {
      let lastRunNodeText = nodeText;
      if (customFunc?.type) {
        return methodType(customFunc, nodeText);
      }
      lastRunNodeText = await normalCall(lineParams, lastRunNodeText, takeLastDisplay, edgeParam as any);
      return lastRunNodeText;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  const getFinalResult = async (
    currentSingleFlow: { type: any; edgeArr: string | any[] },
    nodeText: any,
    edgeParam: { id?: any; target: any; source?: any; lineParam?: any },
    isRecursiveCall = false,
    outerEdgeId: any
  ): Promise<any> => {
    try {
      let prevResult = {
        textAreaValue: nodeText || "",
        displayType: "text",
      } as any;

      // 判定方法类型
      if (currentSingleFlow?.type) {
        return methodType(currentSingleFlow, prevResult.textAreaValue);
      }

      // 处理多个方法调用
      if (!func.isEmpty(currentSingleFlow?.edgeArr)) {
        const flowLen = currentSingleFlow?.edgeArr?.length || 0;

        for (let index = 0; index < currentSingleFlow.edgeArr.length; index++) {
          const x = currentSingleFlow.edgeArr[index];
          const takeLastDisplay = flowLen - 1 === index;
          const edgeParamData = {
            id: outerEdgeId,
            source: x.source,
            target: edgeParam.target,
            lineParam: x.lineParam,
          };
          // 07/28/2023 get latest function name from alias - temp fix by @david but needs to revisit
          const funcName = getFunctionNameByAlias(x.data.lineParam.enterText.replace("/", ""));

          if (selectApiReg.test(x.data.lineParam.enterText)) {
            // 方法调用
            const customFunc = creatorRefStore.singleFlowEdgeArrRef.current.find(x => x.name === funcName);
            const builtInFunctionObj = defaultApis.find(x => x.label === funcName);
            const type = builtInFunctionObj?.value || funcName;
            const isShowStockInterface = type === "showStockInterface";
            // ShowStockInterface需要直接走getOtherTypeData方法
            if (!func.isEmpty(customFunc?.edgeArr) && !isShowStockInterface) {
              // 多层调用
              prevResult = await getFinalResult(customFunc, prevResult.textAreaValue, edgeParam, true, outerEdgeId);
            } else if (!func.isEmpty(customFunc) && !isShowStockInterface) {
              // 自定义方法
              prevResult = await customMethod(
                prevResult.textAreaValue,
                customFunc,
                x.data.lineParam,
                takeLastDisplay,
                edgeParam
              );
            } else {
              // 默认方法
              const imageGenSelectOptionsArr = store.getState().fot.imageGenSelectOptions;
              // 如果权限不可见，但是仍然可以调用这个function
              const selectOption = imageGenSelectOptionsArr.find(x => x.label.trim() === funcName) as any;
              if (func.isEmpty(selectOption)) {
                return {
                  textAreaValue: "",
                  displayType: "text",
                };
              }
              const defaultData = await getOtherTypeData(
                edgeParamData,
                selectOption.value,
                prevResult.textAreaValue,
                true as any
              );

              // 有的类型没有返回值
              if (!func.isEmpty(defaultData)) {
                prevResult = defaultData;
              } else {
                prevResult = {
                  textAreaValue: "",
                };
              }
            }
          } else {
            // 普通调用
            const result = await normalCall(
              x.data.lineParam,
              prevResult.textAreaValue,
              takeLastDisplay,
              edgeParam as any,
              isRecursiveCall
            );
            prevResult = {
              textAreaValue: result || "",
              displayType: "text",
            };
          }
        }
      }
      return prevResult;
    } catch (error) {
      return Promise.reject(error);
    }
  };

  function shardSplittingAll(param: any, text: any, type?: any): void {
    // 获取当前 node
    const currentNode = creatorNodesStore.getNodes().find(x => x.id === param.target) as any;
    if (func.isEmpty(currentNode)) return;
    const isShowStockInterface = type === "showStockInterface";
    const splitArray = isShowStockInterface ? text : splitText("", text).filter((x: any) => !func.isEmpty(x));
    if (func.isEmpty(splitArray)) return;
    creatorRefStore.sliceLevelRef.current = 1;
    const newNodes = [] as any[];
    const groupId = `group-${currentNode.id.replace(/[^\d]/g, "")}`;
    const isCurrGroupExisted = creatorNodesStore.getNodes().some(x => x.id === groupId);
    const childNode = [] as any[];
    let currentGroupChildNodes = [] as any[];
    if (currentNode.type === "customGroup") {
      currentGroupChildNodes = creatorNodesStore.getNodes().filter(x => x.parentNode === groupId);
    }
    // 新增node
    splitArray.forEach((x: string, index: number) => {
      const newNodeId = !func.isEmpty(currentGroupChildNodes[index])
        ? currentGroupChildNodes[index].id
        : `editor-${creatorRefStore.nodeIndexRef.current++}`;
      const newNode = {
        id: newNodeId,
        type: "customNode",
        parentNode: groupId,
        extent: "parent",
        data: {
          ...canvasDataRef.nodeDataRef.current,
          textAreaValue: isShowStockInterface ? x : x.trim(),
          displayType: isShowStockInterface ? "stockInfo" : canvasDataRef.nodeDataRef.current.displayType,
          sliceLevel: creatorRefStore.sliceLevelRef.current,
          isSliceNode: true,
          parentNodeId: groupId,
          childNode: [],
        },
        position: {
          x: distance,
          y: distance * (index + 1) + index * 250,
        },
      };
      childNode.push(newNodeId);
      newNodes.push(newNode);
    });
    // 新增group
    const groupHeight =
      splitArray.length * 250 + ((splitArray.length + 1) * distance) / 2 + projectPageStore.state.previewHeight;
    const newGroupNode = {
      id: groupId,
      type: "customGroup",
      data: {
        ...groupDataRef?.current,
        childNode,
      },
      style: {
        width: 250 + 2 * distance,
        height: groupHeight,
      },
      position: {
        x: isCurrGroupExisted ? currentNode.position.x : currentNode.position.x + 200,
        y: currentNode.position.y,
      },
    };

    // undo监听
    const newSimpleNodes = [newGroupNode, ...newNodes].map(homeMethods.getSimpleNode);
    // 只有来自于切分的param才包含edge所有数据
    const currentNewEdge = (creatorEdgesStore.getEdges().find(x => x.id === param.id) || {}) as any;
    const newEdge = Object.assign(
      {},
      {
        ...currentNewEdge,
        target: groupId,
        data: {
          ...(currentNewEdge?.data || {}),
          lineParam: {
            ...(currentNewEdge?.data?.lineParam || {}),
            loading: false,
          },
        },
      }
    );
    creatorStoreMethods.onNodesEdgesAdd(newSimpleNodes, [homeMethods.getSimpleEdge(newEdge)]);
    // 进入修改流程
    if (currentNode.type === "customGroup") {
      // 替换分组的边
      creatorStoreMethods.replaceGroupEdge(currentNode.data.childNode, newNodes);

      creatorNodesStore.setNodes(prevNodes => {
        // 删除组旧的 node
        // @ts-expect-error 修复ts类型报错
        const parentNodeId = prevNodes.find(x => x.id === currentNode.data.childNode[0]).parentNode;
        const preserveNodes = prevNodes.filter(x => x?.parentNode !== parentNodeId);
        const prevList = preserveNodes.map(l => {
          if (l.id === currentNode.id) {
            return Object.assign({}, { ...l }, { ...newGroupNode });
          }
          return l;
        });
        // 需要删除旧的 node, 添加新拆分的 node
        return [...prevList, ...newNodes];
      });
      return;
    }
    // 改变其类型为分组
    creatorNodesStore.setNodes(prevNodes => {
      const preserveNodes = prevNodes.filter(x => !currentNode.data.childNode.includes(x.id));
      const prevList = preserveNodes.map(l => {
        if (l.id === currentNode.id) {
          return Object.assign({}, { ...l }, { ...newGroupNode });
        }
        return l;
      });
      // 需要删除旧的 node 加入新拆分的 node
      return [...prevList, ...newNodes];
    });

    const allEdgeArr = creatorEdgesStore.getEdges().filter(edge => edge.target === param.target) || [param];
    // 修改边的指向
    creatorEdgesStore.setEdges(prevList => {
      return prevList.map(l => {
        if (allEdgeArr.some(edge => edge.id === l.id)) {
          const newEdge = Object.assign({}, { ...l }, { target: groupId });
          // BSF-1578: https://brain-ai.atlassian.net/browse/BSF-1578
          // 组节点没有上和下的边，但是普通节点点击上和下的边 变成组节点时会丢失边
          return CustomEdgeUtil.assignNodeToGroupNodeHandle(newEdge);
        }
        return l;
      });
    });
    if (groupId === "group-2") {
      setShowTooltip(groupId);
      setCloseToolTipArr(["editor-1", "edge-0"]);
    }
  }

  const handleCreatedFunctionRun = async (
    edgeText: string,
    edgeParam: { id: any; target: any; source: any; lineParam?: any },
    context: any
  ): Promise<undefined> => {
    const { id, target, source } = edgeParam;
    try {
      let customFunctionObj = {} as any;
      let singleFlowEdgeObj = {} as any;
      // project分享functions
      if (!func.isEmpty(customFunctionStore.customFunctionRef.current)) {
        customFunctionObj = customFunctionStore.customFunctionRef.current.find(
          (x: { name: any }) => x.name === edgeText
        );
      }
      // 账号下functions
      if (!func.isEmpty(creatorRefStore.singleFlowEdgeArrRef.current)) {
        singleFlowEdgeObj = creatorRefStore.singleFlowEdgeArrRef.current.find(
          (x: { name: any }) => x.name === edgeText
        );
      }

      const currentSingleFlow = Object.assign({}, customFunctionObj, singleFlowEdgeObj);
      const currentSourceNode = creatorNodesStore.getNodes().find(x => x.id === source);
      if (func.isEmpty(currentSingleFlow)) return;
      const { textAreaValue, displayType } = await getFinalResult(
        currentSingleFlow,
        // @ts-expect-error 修复ts类型报错
        currentSourceNode.data.textAreaValue,
        edgeParam,
        false,
        id
      );
      // 如果为空则在其他地方有直接赋值
      if (func.isEmpty(textAreaValue)) return;

      if (typeof textAreaValue !== "string") {
        textAreaValue.context = JSON.stringify(context);
      }
      creatorNodesStore.setNodes(prevNodes => {
        return prevNodes.map(l => {
          if (l.id === target) {
            return {
              ...l,
              data: {
                ...l.data,
                textAreaValue,
                displayType,
                context,
              },
            };
          }
          return l;
        });
      });
    } catch (error) {
      return Promise.reject(error);
    } finally {
      // 这里需要屏蔽，原因是例如生成图片function会过早停止loading
      // setEdgeLineParam({
      //   id,
      //   loading: false
      // })
    }
  };

  async function postAIDescription(
    query: any,
    identifier: string,
    variables = {},
    abort: boolean,
    isNeedHandelRes = true,
    edgeParam = {}
  ): Promise<any> {
    try {
      let url = "/be/bas-demo-v4/nlp/brain_openai";
      let postParam = {
        engine: "text-davinci-003",
        frequency_penalty: 0,
        max_tokens: 500,
        presence_penalty: 0,
        stop: [],
        prompt: query,
      } as any;
      if (identifier) {
        postParam = await getCompletionsGenerationParam("file", identifier, variables, false);
        url = "/be/bas-demo-v4/nlp/completions_generation";
      }
      const { source = "", target = "", id = "" } = edgeParam as any;
      // @ts-expect-error 修复ts类型报错
      const res = await postData(url, postParam, abort, (postInstance: any) => {
        creatorStoreMethods.setNodeParam({
          id: target,
          data: {
            abortNode: {
              source,
              target,
              edgeId: id,
              apiInstance: postInstance,
            },
          },
        });
      });
      if (!isNeedHandelRes) {
        return res;
      }
      const text = res?.data?.choices?.[0]?.text || res?.data?.choices?.[0]?.message?.content || "";
      const keyValues = text.split("\n").filter((x: any) => !func.isEmpty(x));
      const results = keyValues.map((x: string) => {
        const arr = x.split(": ");
        return {
          title: arr?.[0] || "",
          content: arr?.[1] || "",
        };
      });
      return results;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async function getOtherTypeData(edgeParam: any, type: string, query?: any, isCreatedFunction?: any): Promise<any> {
    const meId = (store.getState() as any).editor.me?.id;
    const selectedTemplateId = (store.getState() as any).studio.selectedTemplate?.id;

    const { target, id, source } = edgeParam;

    const topic = `/studios/${meId}/wsuid_${
      selectedTemplateId ? selectedTemplateId + "_" : ""
    }${target}/midjourneyV2/${Date.now()}`;

    const getStockInfoData = (symbol: any): Promise<any> => {
      return api.getWithErrorCanAbort(`/be/svc-adapter/stock/symbol?text=${symbol}`);
    };
    const getImageApiData = (text: any): Promise<any> => {
      const url = `/be/lambda/function/stableai`;
      return api.postWithErrorCanAbort(url, { prompt: text, json: true });
    };
    const getImageSearchData = (text: any): Promise<any> => {
      const url = `/be/shopping/serpapi/search?engine=google_images&q=${text}`;
      return api.getWithErrorCanAbort(url);
    };
    const getMidjourneyData = (text: any): Promise<any> => {
      const url = `/be/lambda/function/stableai&from=midjourney`;
      return api.postWithErrorCanAbort(url, { prompt: text, json: true });
    };

    const getMidjourneyV2Data = (text: any): Promise<any> => {
      let fetchController: { abort: () => void };
      const callback = (controller: any): void => {
        fetchController = controller;
      };
      let reject = (): void => {};
      let resolve = (): void => {};

      const prePromise = api
        .postWithErrorCanAbort(
          `/be/svc-adapter/midjourney/v2/imagine`,
          {
            topic,
            prompt: text,
          },
          undefined,
          callback as any
        )
        .catch(error => {
          setNodeErrorTip(target, error);
          messageCenter.off(topic, () => {});
          // OPT:
          reject();
          // reject("");
        });

      const promise = new Promise<any>((_resolve, _reject) => {
        reject = _reject;
        resolve = _resolve as any;
        if (topic) {
          messageCenter.on(topic, (res: any) => {
            const imageUrl = get(res, "imageUrls.0");
            if (imageUrl) {
              messageCenter.off(topic, () => {});
              _resolve(imageUrl);
            }
          });
        }
      });

      // @ts-expect-error 修复ts类型报错
      promise.cancel = (): void => {
        fetchController.abort();
        messageCenter.off(topic, () => {});
        // OPT:
        reject();
        // reject("");
      };
      // @ts-expect-error 修复ts类型报错
      promise.resolve = (imageUrl: any): void => {
        messageCenter.off(topic, () => {});
        // @ts-expect-error 修复ts类型报错
        resolve(imageUrl);
      };

      prePromise.then(res => {
        const messageId = get(res, "data.data.messageId");
        creatorStoreMethods.setNodeParam({
          id: target,
          data: {
            displayType: type,
            textAreaValue: { loading: true, messageId, meId },
          },
        });
      });

      return promise;
    };

    const getProteinData = (text: any): Promise<any> => {
      const url = `/be/svc-adapter/alphafold/search?text=${text}`;
      return api.getWithErrorCanAbort(url);
    };

    const getVideoData = (text: any): Promise<any> => {
      const url = `/be/svc-adapter/youtube/v2/search?keyword=${text}`;
      return api.getWithErrorCanAbort(url);
    };

    const getJdShoppingData = (keyword: any): Promise<any> => {
      const url = `/be/svc-adapter/jd/search?keyword=${keyword}`;
      return api.getWithErrorCanAbort(url);
    };

    const getAmazonShoppingData = (keyword: any): Promise<any> => {
      const url = `/be/svc-adapter/purchasing/shop-search?query=${keyword}`;
      return api.getWithErrorCanAbort(url);
    };

    const getWeeeShoppingData = (keyword: any): Promise<any> => {
      const url = `/be/svc-adapter/sayweee/search?keyword=${keyword}`;
      return api.getWithErrorCanAbort(url);
    };
    const getShardSplitting = (query: any, edgeParam: any): Promise<any> => {
      return postAIDescription(
        null,
        "brain_studios_xxx_shard_splitting.model",
        { input: query },
        false,
        false,
        edgeParam
      );
    };
    const getMapData = (query: any): Promise<any> => {
      const url = `/be/bas-demo-v4/nlp/completions_generation`;
      const data = {
        variables: {
          input: query,
        },
        identifier_type: "file",
        identifier_value: "json_list_converter.model",
        domain: "brain_studios",
      };
      return api.postWithErrorCanAbort(url, data);
    };

    const getFoodData = async (query: any): Promise<RestaurantResponseData> => {
      return brainClient.foodApi.getRestaurantList({ query, offset: 0 });
    };

    const chatInterface = (): Promise<{
      data: {
        data: string;
      };
    }> => {
      return Promise.resolve({ data: { data: "interface value" } });
    };

    const apiObj = {
      imageGen: getImageApiData,
      imageSearch: getImageSearchData,
      stockInfo: getStockInfoData, //兼容旧的Stock API可以运行
      showStockInterface: handleStockInterfaceFunc,
      midjourney: getMidjourneyData,
      midjourneyV2: getMidjourneyV2Data,
      protein: getProteinData,
      video: getVideoData,
      jdShopping: getJdShoppingData,
      amazonShopping: getAmazonShoppingData,
      weeeShopping: getWeeeShoppingData,
      SourceOfTruth: getSourceOfTruthResults,
      splitOutput: getShardSplitting,
      showFoodInterface: getFoodData,
      map: getMapData,
      askBrain: getAskBrainData,
      chatInterface: chatInterface,
    };
    const callApi = apiObj[type as keyof typeof apiObj];

    if (typeof callApi !== "function") {
      // 执行自定义创建的func
      await handleCreatedFunctionRun(type, edgeParam as any, query);
      return;
    }

    if (type === "splitOutput") {
      const splitedResult = splitText(query);
      if (splitedResult.length > 1) {
        return new Promise<void>(reslove => {
          shardSplittingAll(edgeParam, query);
          reslove();
        });
      }
    }

    if (type === "map") {
      setReloadMap(false);
    }

    if (IMAGE_TYPES.includes(type)) {
      creatorStoreMethods.setNodeParam({
        id: target,
        data: {
          displayType: type,
          textAreaValue: { loading: true },
        },
      });
    }

    if (STOCK_TYPES.includes(type)) {
      creatorStoreMethods.setNodeParam({
        id: target,
        data: {
          displayType: "stockInfo",
          stockLoading: true,
        },
      });
    }

    const defaultData = {
      showWarning: false,
      showStopGenerateBtn: false,
      abortNode: {},
    };

    // @ts-expect-error 修复ts类型报错
    const currentPromise = callApi(query, edgeParam, defaultData);
    // askBrain不需要重新保存promise cancel
    if (type !== "askBrain") {
      creatorStoreMethods.setNodeParam({
        id: target,
        data: {
          showStopGenerateBtn: true,
          abortNode: {
            source,
            target,
            edgeId: id,
            apiInstance: currentPromise,
          },
        },
      });
    }
    return currentPromise
      .then(
        async (res: {
          data: {
            choices: { text: any }[];
            s3_path: string;
            images_results: any;
            data: { data: Record<string, any>; items: { entryId: any }[]; response: any };
          };
          formatRes: any;
        }) => {
          let param = null;

          if (type === "splitOutput") {
            shardSplittingAll(edgeParam, res?.data?.choices?.[0]?.text);
            return;
          } else if (type === "imageGen" || type === "midjourney") {
            const s3Path = res.data.s3_path || "";
            param = {
              loading: false,
              value: `${settings.S3CDN}${encodeURI(s3Path)}`,
            };
          } else if (type === "midjourneyV2") {
            param = {
              loading: false,
              value: res,
            };
          } else if (type === "imageSearch") {
            // 加载列表中最近一张可用的图片
            param = await utilIndex.loadAvailableImg(res.data?.images_results || [], "original");
          } else if (type === "showStockInterface") {
            if (!edgeParam.lineParam?.isFromGroup) {
              shardSplittingAll(edgeParam, res.formatRes, type);
            }
            return res.formatRes;
          } else if (type === "stockInfo") {
            const result = (res.data?.data?.data || {}) as any;
            param = {
              logo: result.logo,
              name: result.name,
              symbol: result.symbol,
              price: `$ ${result.price}`,
              volume: result.volume,
              businessSummary: result.business_summary,
            };
          } else if (type === "protein") {
            param = {
              type: type,
              value: res?.data?.data?.items?.[0]?.entryId || "",
            };
            creatorStoreMethods.setNodeParam({
              id: target,
              data: {
                ...defaultData,
                displayType: "customJsFunction",
                textAreaValue: param || "",
              },
            });
            return;
          }
          if (type === "video") {
            creatorStoreMethods.setNodeParam({
              id: target,
              data: {
                ...defaultData,
                displayType: type,
                textAreaValue: res.data.data,
              },
            });
            return;
          }
          if (type === "askBrain") {
            return;
          }
          if (type === "jdShopping") {
            creatorStoreMethods.setNodeParam({
              id: target,
              data: {
                ...defaultData,
                displayType: type,
                textAreaValue: res.data.data,
              },
            });
            return;
          }
          if (type === "amazonShopping") {
            creatorStoreMethods.setNodeParam({
              id: target,
              data: {
                ...defaultData,
                displayType: type,
                textAreaValue: res.data.data,
              },
            });

            return;
          }
          if (type === "weeeShopping") {
            creatorStoreMethods.setNodeParam({
              id: target,
              data: {
                ...defaultData,
                displayType: type,
                textAreaValue: res.data.data,
              },
            });
            return;
          }
          if (type === "chatInterface") {
            creatorStoreMethods.setNodeParam({
              id: target,
              data: {
                ...defaultData,
                displayType: type,
                textAreaValue: res.data.data,
              },
            });
            return;
          }
          if (type === "showFoodInterface") {
            creatorStoreMethods.setNodeParam({
              id: target,
              data: {
                displayType: type,
                textAreaValue: res,
              },
            });
            return;
          }
          if (type === "SourceOfTruth") {
            creatorStoreMethods.setNodeParam({
              id: target,
              data: {
                ...defaultData,
                displayType: "text",
                textAreaValue: res.data?.data?.response,
              },
            });
            return;
          }
          if (type === "map") {
            const results = get(res, "data.choices.[0].text", "");
            creatorStoreMethods.setNodeParam({
              id: target,
              data: {
                displayType: type,
                textAreaValue: results,
              },
            });
            setReloadMap(true);
            return;
          }
          if (!isCreatedFunction) {
            creatorStoreMethods.setNodeParam({
              id: target,
              data: {
                ...defaultData,
                displayType: type,
                textAreaValue: param,
              },
            });
          } else {
            return {
              ...defaultData,
              displayType: type,
              textAreaValue: param,
            };
          }
          return res;
        }
      )
      .catch((error: { fromApiError?: any; message?: any; specifyErrorText?: any }) => {
        if (func.isEmpty(error.fromApiError)) {
          const errText = typeof error === "string" ? error : error.message;
          error = {
            specifyErrorText: errText || "Get content failed",
          };
        }
        if (IMAGE_TYPES.includes(type)) {
          creatorStoreMethods.setNodeParam({
            id: target,
            data: {
              textAreaValue: {
                loading: false,
              },
            },
          });
        }
        if (STOCK_TYPES.includes(type)) {
          creatorStoreMethods.setNodeParam({
            id: target,
            data: {
              displayType: "stockInfo",
              stockLoading: false,
            },
          });
        }
        return Promise.reject(error);
      })
      .finally(() => {
        creatorStoreMethods.setNodeParam({
          id: target,
          data: {
            ...defaultData,
          },
        });
      });
  }

  function getSourceOfTruthResults(query: any, edgeParam: any): Promise<any> {
    const requestParam = edgeParam?.lineParam?.requestParam || edgeParam;
    const data = Object.assign({}, requestParam, { query });
    return api.postWithError("/be/vectordb/indexers/", { data }).catch(error => Promise.reject(error));
  }

  // 实现 runall 禁用
  function runAllDisable(): void {
    const edges = creatorEdgesStore.getEdges();
    const nextEdges = edges.filter(x => !projectPageStore.state.alreadyRun.includes(x?.id));
    const nextEdgeIdsArr = nextEdges.map(x => x.id);
    setNeedDisableEdgeIds(nextEdgeIdsArr);
  }

  const postBrainOpenai = async (apiParam: any, onMessage: any): Promise<any> => {
    const { sourceEdgeId, sourceNodeId, textAreaValue, lineParam, targetNodeId } = apiParam;
    try {
      if (feature_tags.enableSkipEmptyVerification && skipEmptyVerification(lineParam?.enterText)) {
        // Skip empty verification
      } else if (func.isEmpty(textAreaValue)) {
        func.customMsg({
          content: "Need input value",
          type: "warning",
        });
        return {};
      }

      // 请求前重置错误提示, 有可能 targetNodeId 并非是节点ID，可能是组，ID需要设置组ID下面的节点ID
      setNodeErrorTip(targetNodeId, {}, true);

      const isUploadFile = await handleUploadFileRun(lineParam.enterText, sourceNodeId, targetNodeId, sourceEdgeId);
      if (isUploadFile) return;

      // 下拉框选择模式
      if (selectApiReg.test(lineParam.enterText)) {
        const edgeParam = {
          id: sourceEdgeId,
          target: targetNodeId,
          source: sourceNodeId,
          lineParam,
        };
        const builtInFunctionObj = defaultApis.find(x => x.label === lineParam.enterText.replace("/", ""));
        const type = builtInFunctionObj?.value || lineParam.enterText.replace("/", "");
        return await getOtherTypeData(edgeParam, type, textAreaValue);
      }
      // 是聊天框类型
      const isChat = await setChatBoxParam({
        edgeId: sourceEdgeId,
        target: targetNodeId,
        lineParams: lineParam,
        setEdgeLineParam: creatorStoreMethods.setEdgeLineParam,
      });
      if (isChat) return;

      // If it is Agent type
      if (feature_tags.showStudiosAgent) {
        const isAgent = await setConnectAgentNode({
          selectValueParam: lineParam?.enterText,
          nodeText: textAreaValue,
          edgeId: sourceEdgeId,
          target: targetNodeId,
          lineParams: lineParam,
          setEdgeLineParam: creatorStoreMethods.setEdgeLineParam,
        });
        if (isAgent) return;
      }
      // 默认模式
      let postParam = {};
      let url = "/be/bas-demo-v4/nlp/brain_openai";
      if (lineParam.lineType === "identifier") {
        url = "/stream/bas-demo-v4/nlp/completions_generation";
        postParam = await getCompletionsGenerationParam("object_id", lineParam.identifier.value, {
          input: textAreaValue,
        });
      } else {
        postParam = getOpenAiParam({
          lineParams: lineParam,
          prompt: lineParam.queryValue || "",
          nodeText: textAreaValue,
        });
      }
      let temp = "";
      await postWithStreaming(
        url,
        postParam,
        (msg: any) => {
          temp += msg.choices?.[0]?.text || msg.choices?.[0]?.delta?.content || "";
          onMessage(targetNodeId, temp);
        },
        false,
        // @ts-expect-error 修复ts类型报错
        (postInstance: any) => {
          creatorStoreMethods.setNodeParam({
            id: targetNodeId,
            data: {
              showStopGenerateBtn: true,
              abortNode: {
                source: sourceNodeId,
                target: targetNodeId,
                edgeId: sourceEdgeId,
                apiInstance: postInstance,
              },
            },
          });
        }
      );
    } catch (error) {
      console.log("postBrainOpenai", error);
      setNodeErrorTip(targetNodeId, error);
    } finally {
      creatorStoreMethods.setNodeParam({
        id: targetNodeId,
        data: {
          showStopGenerateBtn: false,
          abortNode: {},
        },
      });
    }
  };

  const groupPostApi = async (
    apiArr: any[],
    onMessage: (target: any, tempText: any) => void
  ): Promise<any[] | undefined> => {
    if (isEmpty(apiArr)) {
      return;
    }
    let index = 0;
    let results = [] as any[];
    const requestingCounts = 4;
    while (index < apiArr.length) {
      const newArr = apiArr.slice(index, index + requestingCounts).map(x => postBrainOpenai(x, onMessage));
      let edgeIds = apiArr.slice(index, index + requestingCounts).map(x => x.sourceEdgeId);
      // @ts-expect-error 修复ts类型报错
      edgeIds = [...new Set(edgeIds)];
      // 显示run loading
      edgeIds.forEach(x => {
        creatorStoreMethods.setEdgeLineParam({
          id: x,
          loading: true,
        });
      });
      // 已经执行过的数据
      projectPageStore.setAlreadyRunArr([...projectPageStore.state.alreadyRun, ...edgeIds]);
      runAllDisable();
      const currResults = await Promise.allSettled(newArr);
      edgeIds.forEach(x => {
        creatorStoreMethods.setEdgeLineParam({
          id: x,
          loading: false,
        });
      });
      results = [...results, ...currResults];
      index += requestingCounts;
    }
    // TODO: Hardcoded
    const edge = apiArr[0];
    const funcName = edge.lineParam.enterText.replace("/", "");
    const builtInFunctionObj = defaultApis.find(x => x.label === funcName);
    const type = builtInFunctionObj?.value || funcName;
    const isShowStockInterface = type === "showStockInterface";
    if (results.length > 0 && isShowStockInterface) {
      const resData = results
        .filter(x => x.status === "fulfilled")
        .map(x => x.value)
        .flat();
      shardSplittingAll(edge, resData, "showStockInterface");
    }
    return results;
  };

  const handleProjectData = useCallback(
    (
      selectedExistingProject: any
    ): {
      nodeIndex: any;
      edgeIndex: any;
      nodesFromProject: any;
      edgesFromProject: any;
      UIData: any;
      variablesList: any;
      customFunctions: any;
    } => {
      let nodesFromProject = "" as any;
      if (typeof selectedExistingProject?.v2?.nodes === "string") {
        nodesFromProject = JSON.parse(selectedExistingProject?.v2?.nodes);
      }
      if (Array.isArray(selectedExistingProject?.v2?.nodes)) {
        nodesFromProject = selectedExistingProject?.v2?.nodes;
      }

      let edgesFromProject = "" as any;
      if (typeof selectedExistingProject?.v2?.edges === "string") {
        edgesFromProject = JSON.parse(selectedExistingProject?.v2?.edges);
      }
      if (Array.isArray(selectedExistingProject?.v2?.edges)) {
        edgesFromProject = selectedExistingProject?.v2?.edges;
      }

      let variablesList = [];
      if (typeof selectedExistingProject?.v2?.variablesList === "string") {
        variablesList = JSON.parse(selectedExistingProject?.v2?.variablesList);
      }

      let UIData = [];
      if (!func.isEmpty(selectedExistingProject?.v2?.UI)) {
        UIData = selectedExistingProject.v2.UI.map((x: any) => {
          let inputObj = x.input;
          let outputArr = x.output;
          if (!func.isEmpty(x.input)) {
            if (typeof x.input === "string") {
              inputObj = JSON.parse(x.input);
            } else {
              inputObj = inputObj.map((x: any) => {
                const nodeData = HomeUsePluginUtil.getFunctionData(canvasDataRef.nodeDataRef.current);
                const data = Object.assign({}, x.data, { ...nodeData });
                return Object.assign({}, x, { data });
              });
            }
          }
          if (!func.isEmpty(x.output) && typeof x.output === "string") {
            outputArr = JSON.parse(x.output);
          }

          let titleStyle = {};
          if (!func.isEmpty(x.titleStyle) && typeof x.titleStyle === "string") {
            titleStyle = JSON.parse(x.titleStyle);
          }

          let subTitleStyle = {};
          if (!func.isEmpty(x.subTitleStyle) && typeof x.subTitleStyle === "string") {
            subTitleStyle = JSON.parse(x.subTitleStyle);
          }

          let showTitle = x?.showTitle;
          if (typeof x?.showTitle != "boolean") {
            showTitle = true;
          }
          let showSubTitle = x?.showSubTitle;
          if (typeof x?.showSubTitle != "boolean") {
            showSubTitle = true;
          }

          return {
            ...x,
            input: inputObj,
            output: outputArr,
            titleStyle,
            subTitleStyle,
            showTitle,
            showSubTitle,
          };
        });
      }

      const nodeIndex = selectedExistingProject.v2?.nodeIndex;
      const edgeIndex = selectedExistingProject.v2?.edgeIndex;
      if (!nodesFromProject || !edgesFromProject || isNaN(nodeIndex) || isNaN(edgeIndex)) {
        throw Error("Failed to load from project, incorrect data format");
      }
      nodesFromProject = nodesFromProject.map((x: any) => {
        let text = x.data?.textAreaValue;
        if (typeof text === "string") {
          text = x.data?.textAreaValue.trim();
        }
        const nodeData = GROUPID_REG.test(x.id) ? groupDataRef?.current : canvasDataRef.nodeDataRef.current;
        const data = Object.assign({}, { ...nodeData }, x.data, { textAreaValue: text });
        return Object.assign({}, x, { data });
      });

      edgesFromProject = edgesFromProject.map((x: any) => {
        const data = Object.assign({}, { ...edgeDataRef?.current }, x.data, { isFromSaveData: true });
        return Object.assign({}, x, {
          // 由于edge连接形式更改，兼容旧数据指定默认edge起始/终点为左右连接
          sourceHandle: func.isEmpty(x.sourceHandle) ? "b" : x.sourceHandle,
          targetHandle: func.isEmpty(x.targetHandle) ? "a" : x.targetHandle,
          data,
          animated: false,
          type: "customEdge",
        });
      });
      const hasOldSliceData = edgesFromProject.some((x: any) => x.data.isCreatedBySlice && !GROUPID_REG.test(x.target));
      if (hasOldSliceData) {
        func.customMsg({
          content:
            "Your project is using a older version of sharding that is no longer supported, please remove your splitted nodes and redo sharding for your nodes.",
          type: "warning",
        });
      }

      let customFunctions = [];
      if (!func.isEmpty(selectedExistingProject?.v2?.functions)) {
        customFunctions = selectedExistingProject.v2.functions.map((x: any) => {
          if (!func.isEmpty(x.edgeArr)) {
            const newEdgeArr = x.edgeArr.map((y: any) => {
              return {
                ...y,
                data: {
                  ...edgeDataRef?.current,
                  ...y.data,
                },
              };
            });
            return {
              ...x,
              edgeArr: newEdgeArr,
            };
          }
          return x;
        });
      }

      return {
        nodeIndex,
        edgeIndex,
        nodesFromProject,
        edgesFromProject,
        UIData,
        variablesList,
        customFunctions,
      };
    },
    [edgeDataRef, groupDataRef]
  );

  const getEdgeGeneration = useCallback(
    async (aiValue: any, id: string) => {
      // const query = `The system generates a system description based on the Description of the AI.
      //   ###
      //   Description of AI: AI that generates HTML and inline CSS code based on a description of a UI.
      //   System  Description: You are an AI that generates HTML and inline CSS code based on a given description of a UI.
      //   ###
      //   Description of AI: ${aiValue}
      //   System Description:`
      const postParam = (await getCompletionsGenerationParam(
        "file",
        "brain_studios_edge_prompt_generation.model",
        {
          input: aiValue,
        },
        false,
        "brain_studios"
      )) as any;
      // todo: 这里可能还要处理新的边的类型的情况
      // cot额外需要传递变量

      // creatorRefStore.isNewEdgeRunRef.current v3
      const lineParam = creatorRefStore.isNewEdgeRunRef.current
        ? creatorNodesStore.getNodes().find(x => x.id === id)?.data.lineParam
        : creatorEdgesStore.getEdges().find(x => x.id === id)?.data.lineParam;
      // v2才传递边上的变量
      if (creatorRefStore.isNewEdgeRunRef.current === false) {
        if (lineParam && !isEmpty(lineParam.varilableNames)) {
          postParam.extra_inputs = arrayKeyToObject(lineParam.varilableNames || []);
        }
      }

      // 多连线不创建变量 直接获取sourceNode的title
      if (creatorRefStore.isNewEdgeRunRef.current) {
        const edgeNode = creatorNodesStore.getNodes().find(x => x.id === id) as any;
        const sourceIdArr = edgeNode.data.flows.map((x: { sourceNodeId: any }) => x.sourceNodeId);
        if (sourceIdArr.length > 1) {
          // multiple input
          const sourceLabels = creatorNodesStore
            .getNodes()
            .filter(x => sourceIdArr.includes(x.id))
            .map(x => x.data.inputTitle || x.data.generatedLabel || "Input");

          postParam.extra_inputs = Object.assign(postParam.extra_inputs || {}, arrayKeyToObject(sourceLabels));
        }
      }

      const res = await postData("/be/bas-demo-v4/nlp/prompt_generation", postParam, false);
      if (res.status !== 200 || !res?.data?.identifier) {
        func.messageUtil("Failed to create AI");
        console.error("Failed to generate prompt", res.statusCode);
        return;
      }
      const resultQuery = res?.data?.identifier;
      return resultQuery;
    },
    [getCompletionsGenerationParam, postData]
  );

  const getAndSetQuery = useCallback(
    async (id: any, text: any) => {
      try {
        creatorEdgesStore.setQueryLoading(id, "getQueryLoading", true);
        const identifier = await getEdgeGeneration(text, id);
        creatorEdgesStore.setQueryLoading(id, "getQueryLoading", false);
        if (identifier) {
          creatorEdgesStore.setEdges((prevList: any[]) => {
            return prevList.map((l: { id: any; data: { lineParam: any } }) => {
              if (l.id === id) {
                return {
                  ...l,
                  data: {
                    ...l.data,
                    lineParam: {
                      ...l.data.lineParam,
                      lineType: "identifier",
                      identifier,
                      enterText: text,
                    },
                  },
                };
              }
              return l;
            });
          });
          creatorEdgesStore.setQueryLoading(id, "isGetQuery", true);
        }
        return {
          lineType: "identifier",
          identifier,
        };
      } catch (error) {
        func.messageUtil(error);
        console.error("get And Set Query", error);
        creatorEdgesStore.setQueryLoading(id, "getQueryLoading", false);
      }
    },
    [getEdgeGeneration]
  );

  const groupChanged = (id: any): void => {
    creatorStoreMethods.setEdgeLineParam({
      id: id,
      loading: true,
    });
    // opt:
    // const nodes = creatorNodesStore.getNodes();
    // const edges = creatorEdgesStore.getEdges();
    const nodes = creatorNodesStore.getNodes();
    const edges = creatorEdgesStore.getEdges();

    const groupEdges = edges.find((x: { id: any }) => x.id === id) as any;
    const delGroupNode = nodes.find((x: { id: any }) => x.id === groupEdges.target) as any;
    const nodeTextAreaValue = nodes.find((x: { id: any }) => x.id === groupEdges.source) as any;
    const groupChildNode = delGroupNode?.data?.childNode || [];
    const newNodeId = `editor-${creatorRefStore.nodeIndexRef.current++}`;

    const newNode = {
      id: newNodeId,
      type: "customNode",
      data: {
        ...canvasDataRef.nodeDataRef.current,
      },
      position: {
        x: delGroupNode.position.x,
        y: delGroupNode.position.y,
      },
    };

    // 分组被改变
    creatorNodesStore.setNodes(prevNodes => {
      const prevList = prevNodes.filter(x => x.id !== groupEdges.target && !groupChildNode.includes(x.id));
      return [...prevList, newNode];
    });

    // 更改子node链接父级
    creatorEdgesStore.setEdges(prevEdges => {
      const prevList = prevEdges.filter(x => !groupChildNode.includes(x.source));
      return prevList.map(x => {
        if (x.id === groupEdges.id) {
          return Object.assign({}, { ...x }, { target: newNodeId });
        }
        return x;
      });
    });

    const edgesData = creatorEdgesStore.getEdges().find(x => x.id === groupEdges.id);

    const apiParam = {
      lineParam: edgesData.data.lineParam,
      sourceEdgeId: edgesData.id,
      sourceNodeId: edgesData.source,
      targetNodeId: edgesData.target,
      textAreaValue: nodeTextAreaValue.data.textAreaValue,
    };
    postBrainOpenai(apiParam, (target: any, tempText: string) => {
      creatorNodesStore.setNodes(prevList => {
        return prevList.map(l => {
          if (l.id === target) {
            // 自动判定获取内容 displayType
            const displayType = getAutomaticDisplayType(l.data, tempText);
            return {
              ...l,
              data: {
                ...l.data,
                textAreaValue: tempText.trim(),
                displayType: displayType,
              },
            };
          }
          return l;
        });
      });
    });
    creatorStoreMethods.setEdgeLineParam({
      id: id,
      loading: false,
    });
  };

  // opt:
  // const subscriberParamsData = useSelector((state: any) => state.editor.subscriberParamsData);
  const subscriberParamsData = store.getState().editor.subscriberParamsData as any;
  const [searchParams] = useSearchParams();
  const isSubscriber =
    subscriberParamsData?.attributes?.mode === "subscriber" || searchParams.get("mode") === "subscriber";

  const postImagicaStudioApi = useCallback(
    (obj: any) => {
      // opt:
      const userMe = store.getState().editor.me as any;

      if (userMe?.id && !isSubscriber && creatorRefStore.isLoadPreferences.current) {
        const data = { key: "imagica_studio_tutorial", value: JSON.stringify(obj) };
        api.postWithError(`/api/users/me/preferences.json`, data);
      }
    },
    [isSubscriber]
  );

  return {
    sendRequest,
    methodType,
    normalCall,
    customMethod,
    getFinalResult,
    shardSplittingAll,
    handleCreatedFunctionRun,
    postAIDescription,
    getOtherTypeData,
    getSourceOfTruthResults,
    runAllDisable,
    postBrainOpenai,
    groupPostApi,
    handleProjectData,
    getEdgeGeneration,
    getAndSetQuery,
    groupChanged,
    postImagicaStudioApi,
  };
}
